import {
  Checkbox,
  SeverityDisplay,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import React from 'react';
import { Box, Center, Flex, Image } from '@chakra-ui/react';
import styled from '@emotion/styled';

const DEFAULT_IMAGE = '../../cutlery.png';

const PressableCard = styled('div')<{ editing: boolean }>`
  display: contents;
  height: 100%;

  &:hover {
    cursor: ${(p) => (p.editing ? 'pointer' : null)};
  }
`;

type Props = {
  active?: boolean;
  bgColor?: string;
  dashedBorder?: boolean;
  disableSeverityBar?: boolean;
  disableSeverityColor?: boolean;
  disableSeverityLabel?: boolean;
  editing?: boolean;
  foodId: string;
  foodLabel: string;
  imgURL?: string;
  onChange?: (foodId: string, checked: boolean) => void;
  selected?: boolean;
  severityLabel?: string;
  severityLevel?: number;
};

/**
 * Food list tile with image, label, optional severity indicator, and
 * an edit-mode selection checkbox. Used in the diet / trigger-foods
 * editors on the patient overview.
 *
 * Modes:
 *   - `editing=false` (default) — display only.
 *   - `editing=true` — clicking toggles `selected` via `onChange`.
 *
 * @example
 * <FoodCard
 *   foodId="apple"
 *   foodLabel="Apple"
 *   imgURL={apple.image}
 *   severityLabel="High trigger"
 *   severityLevel={3}
 *   editing
 *   selected={selectedFoods.includes('apple')}
 *   onChange={toggleFood}
 * />
 */
export const FoodCard = ({
  active = false,
  bgColor,
  dashedBorder = false,
  disableSeverityBar = false,
  disableSeverityColor = false,
  disableSeverityLabel = false,
  editing = false,
  foodId,
  foodLabel,
  imgURL = DEFAULT_IMAGE,
  onChange = () => null,
  selected,
  severityLabel = '',
  severityLevel = 0,
}: Props) => {
  return (
    <VStack
      mr={'20px'}
      rounded="md"
      w={'140px'}
      flexShrink={0}
      bgColor={bgColor}
      borderWidth={selected ? 3 : 1}
      borderColor={selected ? 'primary.700' : undefined}
      borderStyle={dashedBorder ? 'dashed' : 'solid'}
      h={'auto'}
      position={'relative'}
    >
      <PressableCard
        editing={editing}
        onClick={() => {
          if (!editing) return;
          onChange(foodId, !selected);
        }}
      >
        {editing && (
          <Checkbox
            position={'absolute'}
            my={'auto'}
            top={3}
            left={3}
            justifyContent={'flex-end'}
            isChecked={selected}
            value={foodId}
            borderWidth={1}
            borderRadius={1}
            borderColor={'warmGray.900'}
          />
        )}

        <Box p={2} flex={1}>
          <Center>
            <VStack alignItems="center">
              <Flex w={'56px'} flexDir={'column'} alignItems={'center'} my={4}>
                <Image
                  boxSize={'32px'}
                  src={imgURL}
                  alt={`${foodLabel}-icon`}
                />
              </Flex>

              <Text
                color={'evergreen'}
                fontWeight={'normal'}
                textAlign={'center'}
              >
                {foodLabel}
              </Text>
            </VStack>
          </Center>
        </Box>

        {!disableSeverityBar && (
          <Box
            bgColor={
              disableSeverityColor
                ? 'transparent'
                : active
                ? 'primary.500'
                : 'muted.400'
            }
            borderBottomRadius={selected ? 3 : 5}
            borderTopStyle={dashedBorder ? 'dashed' : 'solid'}
            borderTopWidth={1}
            minH={'44px'}
            p={2}
            display={'flex'}
            justifyContent={'center'}
          >
            {!disableSeverityLabel && (
              <Center>
                <SeverityDisplay label={severityLabel} level={severityLevel} />
              </Center>
            )}
          </Box>
        )}
      </PressableCard>
    </VStack>
  );
};
